import {
  useCallback,
  useState,
  useMemo,
  ReactElement,
  FC,
  useEffect,
  memo,
} from 'react'
import styled from 'styled-components'
// @ts-ignore
import { Scrollama, Step } from 'react-scrollama'
import { useStore } from 'hooks/useStore'
import { Canvas } from 'components/canvas'
import { Strong, ScrollytellingStepContent } from 'components/styles'
import { useSpring, animated, config } from '@react-spring/web'
import { device } from '../breakpoints'
import Header from 'components/Header'
import { TextWidth } from '@ta-interaktiv/react-disco-grid'
import { Trans, useTranslation } from 'react-i18next'
import { throttle } from 'throttle-debounce'

const CanvasScrolly = ({
  title,
  keywords,
  date,
  authors,
  lead,
}: {
  title: string
  keywords: string
  date: string
  authors: string
  lead?: string
}) => {
  const t = useTranslation().t
  const [canvasContainerRef, setCanvasContainerRef] =
    useState<HTMLDivElement | null>(null)
  const { percentage, setPercentage, stage, setStage } = useStore()
  const canvas = useMemo(() => <Canvas />, [])

  const handleEnter = (step: any) => {
    if (step.data >= 1 && step.data < 5) {
      setStage(step.data)
    }
  }

  const handleExit = (step: any) => {
    if (step.data === 1 && step.direction === 'up') {
      setStage(0)
    }
  }

  const handleProgress = (response: any) => {
    setPercentage((response.progress + response.data - 1) * 0.25)
  }

  const throttledHandleScroll = useCallback(throttle(50, handleProgress), [])
  const h = canvasContainerRef?.getBoundingClientRect().height || 0
  const canvasContainerHeight = Math.round(h)
  const MemoizedSpecialStep = useMemo(() => SpecialStep, [])

  return (
    <>
      {/* The canvas */}
      <ChartContainer height={`calc(${canvasContainerHeight}px + 0px)`}>
        <ChartContent>{canvas}</ChartContent>
      </ChartContainer>

      {/* The texts */}
      <Container
        style={{ zIndex: stage === 0 ? 0 : 2 }}
        ref={setCanvasContainerRef}
      >
        {process.env.NODE_ENV !== 'production' && (
          <Debug>
            {percentage.toFixed(2)} {stage} ({((percentage * 4) % 1).toFixed(2)}
            )
          </Debug>
        )}

        <ScrollytellingContainer>
          <ScrollytellingStepFirst>
            <ScrollytellingStepContent style={{ marginBottom: '30vh' }} wide>
              <Header
                title={title}
                keywords={keywords}
                authors={authors}
                lead={lead}
              />
            </ScrollytellingStepContent>
          </ScrollytellingStepFirst>
          <ScrollytellingStep>
            <ScrollytellingStepContent>
              <p>
                <Trans
                  i18nKey={'intro:scrollytelling:step1:paragraph1'}
                  components={{ strong: <Strong /> }}
                />
              </p>
              <p>{t('intro:scrollytelling:step1:paragraph2')}</p>
            </ScrollytellingStepContent>
          </ScrollytellingStep>
          <Scrollama
            offset={1}
            onStepEnter={handleEnter}
            onStepExit={handleExit}
            onStepProgress={throttledHandleScroll}
          >
            <Step data={1}>
              <ScrollytellingStep>
                <ScrollytellingStepContent>
                  <p>{t('intro:scrollytelling:step2:paragraph1')}</p>
                </ScrollytellingStepContent>
              </ScrollytellingStep>
            </Step>
            <Step data={2}>
              <ScrollytellingStep>
                <ScrollytellingStepContent>
                  <MemoizedSpecialStep
                    height={'50vh'}
                    offset={0.35}
                    pctOffset={0.25}
                    firstElement={
                        <p>
                          <Trans
                            i18nKey={'intro:scrollytelling:step3:paragraph1'}
                            components={{
                              strong: <Strong />,
                              span: <span style={{ whiteSpace: 'nowrap' }} />,
                            }}
                          />
                        </p>}
                    secondElement={<p>{t('intro:scrollytelling:step3:paragraph2')}</p>}
                  />
                </ScrollytellingStepContent>
              </ScrollytellingStep>
            </Step>
            <Step data={3}>
              <ScrollytellingStep>
                <ScrollytellingStepContent>
                  <MemoizedSpecialStep
                    height={'50vh'}
                    offset={0.35}
                    pctOffset={0.5}
                    firstElement={
                          <Trans
                            i18nKey={'intro:scrollytelling:step4:paragraph1'}
                            components={{
                              strong: <Strong />,
                              span: <span style={{ whiteSpace: 'nowrap' }} />,
                            }}
                          />}
                    secondElement={
                        <p>{t('intro:scrollytelling:step4:paragraph2')}</p>}
                  />
                </ScrollytellingStepContent>
              </ScrollytellingStep>
            </Step>
            <Step data={4}>
              <ScrollytellingStep>
                <ScrollytellingStepContent>
                  <p>
                    <Trans
                      i18nKey={'intro:scrollytelling:step5:paragraph1'}
                      components={{ strong: <Strong /> }}
                    />
                  </p>
                </ScrollytellingStepContent>
              </ScrollytellingStep>
            </Step>
          </Scrollama>
        </ScrollytellingContainer>
        <TextWidth>
          <Header date={date} />
        </TextWidth>
      </Container>
    </>
  )
}

const Title = styled.h1`
  font-family: var(--font-serif);
  font-size: 2.5em;
  line-height: 1.2;
`

const Debug = styled.div`
  position: fixed;
  top: 50px;
  left: 0;
  z-index: 100;
`

const Container = styled.div`
  position: relative;
  padding-top: 25vh;
`

const ChartContainer = styled.div<{ height: string }>`
  z-index: 1;
  width: 100%;
  top: 0;
  left: 0;
  min-height: 100vh;
  position: absolute;
  transform: translateZ(0);
  backface-visibility: hidden;
  height: ${({ height }) => height};
`

const ChartContent = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
`

const ScrollytellingContainer = styled.div`
  position: relative;
  z-index: 0;
  // pointer-events: none;
`

const ScrollytellingStep = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
`

const ScrollytellingStepFirst = styled(ScrollytellingStep)``

interface SpecialStepProps {
  firstElement: ReactElement | ReactElement[]
  secondElement: ReactElement | ReactElement[]
  height: string | number
  offset: number
  pctOffset: number
}

const SpecialStep: FC<SpecialStepProps> = memo(
  ({ firstElement, secondElement, height, offset, pctOffset }) => {
    const [paddingTop, setPaddingTop] = useState(0)
    const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)
    const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null)
    const [isFixed, setIsFixed] = useState(false)
    const [spring, api] = useSpring(() => ({ val: 0, config: config.default }))

    const handleEnter = (step: any) => {
      setIsFixed(true)
    }

    const handleExit = (step: any) => {
      setIsFixed(false)

      if (step.direction === 'down') {
        setPaddingTop(containerEl?.getBoundingClientRect().height || 0)
      } else {
        setPaddingTop(0)
      }
    }

    useEffect(() => {
      useStore.subscribe(({ percentage }) => {
        api.start({
          val: Math.min(
            1,
            Math.max(0, (percentage - pctOffset - 0.065) * 10.25)
          ),
        })
      })
    }, [])

    return (
      <Scrollama
        offset={offset}
        onStepEnter={handleEnter}
        onStepExit={handleExit}
      >
        <Step>
          <div style={{ position: 'relative', width: '100%' }}>
            <div
              style={{
                height: `calc(${height} - 0px)`,
                width: '100%',
                overflow: 'hidden',
                marginBottom: contentEl?.getBoundingClientRect().height,
              }}
              ref={setContainerEl}
            />
            <SpecialStepContent
              ref={setContentEl}
              fixed={isFixed}
              paddingTop={paddingTop}
              offset={offset}
            >
              {firstElement}
              <animated.div
                style={{
                  opacity: spring.val,
                  transform: spring.val.to(
                    (v) => `translateY(${(1 - v) * 400}px)`
                  ),
                }}
              >
                {secondElement}
              </animated.div>
            </SpecialStepContent>
          </div>
        </Step>
      </Scrollama>
    )
  }
)

const SpecialStepContent = styled.div<{
  fixed: boolean
  paddingTop: number
  offset: number
}>`
  position: ${({ fixed }) => (fixed ? 'fixed' : 'absolute')};
  top: ${({ fixed, offset }) => (fixed ? `${offset * 100}%` : '0')};
  left: ${({ fixed }) => (fixed ? '50%' : '0')};
  transform: ${({ fixed }) =>
    fixed ? 'translate(-50%, 0)' : 'translate(0, 0)'};
  margin-top: ${({ paddingTop, fixed }) => (fixed ? 0 : paddingTop + 'px')};
  width: 100%;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    max-width: ${({ fixed }) => (fixed ? 'calc(100vw - 2em)' : 'none')};
  }

  @media ${device.tablet}, @media ${device.laptop} {
    max-width: ${({ fixed }) => (fixed ? '500px' : 'none')};
  }
`

export default CanvasScrolly
